// /host/events/:id/manage — host's manage view for a single event.
//
// Shows the event header, a list of incoming interests, and the actions
// available given the current state. Drafts surface a small inline edit form;
// published events surface accept/decline per interest plus close + cancel
// actions.

import {
  Alert,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { createFileRoute,Link } from '@tanstack/react-router';
import type { ArtFormGroup } from '@wwaave/shared/vocab';
import { useEffect, useState } from 'react';

import { AppShell } from '../../../components/app-shell/AppShell';
import { StateBadge } from '../../../components/StateBadge';
import { csrfHeaders } from '../../../lib/auth-context';
import type { AnyState } from '../../../lib/display-state';
import { showToast } from '../../../lib/toast';

export const Route = createFileRoute('/host/events/$id/manage')({
  component: HostEventManagePage,
});

type EventDetail = {
  event: {
    id: string;
    title: string;
    description: string | null;
    artFormId: string | null;
    startsAt: number;
    endsAt: number;
    slotsTotal: number;
    slotsFilled: number;
    slotsRemaining: number;
    payCents: number | null;
    payNotes: string | null;
    state: AnyState['event'];
    publishedAt: number | null;
    createdAt: number;
    updatedAt: number;
  };
  venue: { id: string; slug: string; name: string; city: string; country: string };
  host: { id: string };
  viewer: {
    isOwner: boolean;
    isAdmin: boolean;
    myInterest: { id: string; state: AnyState['event_interest'] } | null;
  };
};

type ArtFormRow = {
  id: string;
  slug: string;
  name: string;
  group: ArtFormGroup;
};

type InterestItem = {
  id: string;
  state: AnyState['event_interest'];
  message: string | null;
  createdAt: number;
  exchangeId: string | null;
  artist: {
    id: string;
    displayName: string;
    slug: string | null;
    profilePhotoUrl: string | null;
    bioExcerpt: string | null;
  };
};

function HostEventManagePage() {
  const { id } = Route.useParams();
  const [data, setData] = useState<EventDetail | null>(null);
  const [interests, setInterests] = useState<InterestItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [acceptOpen, { open: openAccept, close: closeAccept }] = useDisclosure(false);
  const [acceptingInterest, setAcceptingInterest] = useState<InterestItem | null>(null);
  const [acceptStartDate, setAcceptStartDate] = useState('');
  const [acceptEndDate, setAcceptEndDate] = useState('');

  const [declineOpen, { open: openDecline, close: closeDecline }] = useDisclosure(false);
  const [decliningInterest, setDecliningInterest] = useState<InterestItem | null>(null);
  const [declineReason, setDeclineReason] = useState('');

  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editArtFormId, setEditArtFormId] = useState('');
  const [artForms, setArtForms] = useState<ArtFormRow[] | null>(null);
  const [editStarts, setEditStarts] = useState('');
  const [editEnds, setEditEnds] = useState('');
  const [editSlots, setEditSlots] = useState<number | ''>(1);
  const [editPayDollars, setEditPayDollars] = useState<number | ''>('');
  const [editPayNotes, setEditPayNotes] = useState('');

  const refresh = async (): Promise<void> => {
    try {
      const detailRes = await fetch(`/api/events/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      const detailJson = (await detailRes.json()) as
        | ({ ok: true } & EventDetail)
        | { ok: false; error?: { message?: string } };
      if (!detailRes.ok || !detailJson.ok) {
        const msg =
          'error' in detailJson && detailJson.error?.message
            ? detailJson.error.message
            : "Couldn't load this event.";
        setError(msg);
        return;
      }
      setData(detailJson);

      const intRes = await fetch(
        `/api/events/${encodeURIComponent(id)}/interests`,
        { credentials: 'include' },
      );
      const intJson = (await intRes.json()) as {
        ok: boolean;
        items?: InterestItem[];
        error?: { message?: string };
      };
      if (!intRes.ok || !intJson.ok) {
        setError(intJson.error?.message ?? "Couldn't load interests.");
        return;
      }
      setInterests(intJson.items ?? []);
      setError(null);
    } catch {
      setError('Network problem loading the event.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Seed the edit form whenever the modal opens
  useEffect(() => {
    if (editOpen && data !== null) {
      setEditTitle(data.event.title);
      setEditDescription(data.event.description ?? '');
      setEditArtFormId(data.event.artFormId ?? '');
      setEditStarts(localFromMs(data.event.startsAt));
      setEditEnds(localFromMs(data.event.endsAt));
      setEditSlots(data.event.slotsTotal);
      setEditPayDollars(
        data.event.payCents !== null ? data.event.payCents / 100 : '',
      );
      setEditPayNotes(data.event.payNotes ?? '');
    }
  }, [editOpen, data]);

  // Pull the venue's accepted art forms once we have a venue id, so the edit
  // modal's picker is ready when the host opens it.
  useEffect(() => {
    if (data === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/events/art-forms?venue_id=${encodeURIComponent(data.venue.id)}`,
          { credentials: 'include' },
        );
        const json = (await res.json()) as { ok: boolean; items?: ArtFormRow[] };
        if (cancelled) return;
        if (res.ok && json.ok && Array.isArray(json.items)) {
          setArtForms(json.items);
        }
      } catch {
        // Optional picker — fall through with no-op.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  async function eventAction(label: string, path: string, body?: Record<string, unknown>): Promise<void> {
    setActing(label);
    setActionError(null);
    try {
      const res = await fetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body ?? {}),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't apply that change.");
        return;
      }
      showToast({ message: `${label} done.`, variant: 'success' });
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setActing(null);
    }
  }

  async function saveEdit(): Promise<void> {
    setActing('Save');
    setActionError(null);
    try {
      const startsMs = new Date(editStarts).getTime();
      const endsMs = new Date(editEnds).getTime();
      if (!Number.isFinite(startsMs) || !Number.isFinite(endsMs)) {
        setActionError("Those dates didn't parse.");
        return;
      }
      const body: Record<string, unknown> = {
        title: editTitle.trim(),
        description: editDescription.trim(),
        starts_at: startsMs,
        ends_at: endsMs,
        slots_total: typeof editSlots === 'number' ? editSlots : 1,
        pay_notes: editPayNotes.trim(),
      };
      if (editArtFormId.length > 0) body['art_form_id'] = editArtFormId;
      if (typeof editPayDollars === 'number' && editPayDollars > 0) {
        body['pay_cents'] = Math.round(editPayDollars * 100);
      }
      const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't save.");
        return;
      }
      showToast({ message: 'Saved.', variant: 'success' });
      closeEdit();
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setActing(null);
    }
  }

  async function confirmAccept(): Promise<void> {
    if (acceptingInterest === null) return;
    setActing('Accept');
    setActionError(null);
    try {
      const res = await fetch(
        `/api/events/interests/${encodeURIComponent(acceptingInterest.id)}/accept`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({
            decision: 'accept',
            start_date: acceptStartDate,
            end_date: acceptEndDate,
          }),
        },
      );
      const json = (await res.json()) as {
        ok: boolean;
        exchangeId?: string;
        error?: { message?: string };
      };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't accept.");
        return;
      }
      showToast({ message: 'Accepted. Exchange opened.', variant: 'success' });
      closeAccept();
      setAcceptingInterest(null);
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setActing(null);
    }
  }

  async function confirmDecline(): Promise<void> {
    if (decliningInterest === null) return;
    setActing('Decline');
    setActionError(null);
    try {
      const body: Record<string, unknown> = { decision: 'decline' };
      if (declineReason.trim().length > 0) body['reason'] = declineReason.trim();
      const res = await fetch(
        `/api/events/interests/${encodeURIComponent(decliningInterest.id)}/decline`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify(body),
        },
      );
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't decline.");
        return;
      }
      showToast({ message: 'Declined.', variant: 'success' });
      closeDecline();
      setDecliningInterest(null);
      setDeclineReason('');
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setActing(null);
    }
  }

  if (error !== null) {
    return (
      <AppShell tone="content">
        <Box style={{ maxWidth: 640, marginInline: 'auto' }}>
          <Alert color="red">{error}</Alert>
          <Group mt="md">
            <Anchor component={Link} to="/host/events">
              Back to events
            </Anchor>
          </Group>
        </Box>
      </AppShell>
    );
  }
  if (data === null || interests === null) {
    return (
      <AppShell tone="content">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </AppShell>
    );
  }

  const { event, venue } = data;
  const isDraft = event.state === 'draft';
  const isPublished = event.state === 'published';
  const startIso = isoDateOnly(event.startsAt);
  const endIso = isoDateOnly(event.endsAt);

  const pendingInterests = interests.filter((i) => i.state === 'submitted');
  const resolvedInterests = interests.filter((i) => i.state !== 'submitted');

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Stack gap={4}>
            <Text size="sm" c="dimmed" tt="uppercase">
              Event at {venue.name}
            </Text>
            <Title order={1}>{event.title}</Title>
            <Text c="dimmed">
              {formatRange(event.startsAt, event.endsAt)} · {event.slotsFilled}/{event.slotsTotal}{' '}
              filled
            </Text>
          </Stack>
          <Stack gap="xs" align="flex-end">
            <StateBadge entity="event" state={event.state} size="lg" />
            <Link
              to="/events/$id"
              params={{ id: event.id }}
              style={{ textDecoration: 'underline', fontSize: 14 }}
            >
              View public page
            </Link>
          </Stack>
        </Group>

        {actionError !== null ? <Alert color="red">{actionError}</Alert> : null}

        <Grid gap="lg" align="flex-start">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="xl">
              <Stack gap="sm">
                <Group justify="space-between" align="baseline">
                  <Text fw={600} size="sm" tt="uppercase" c="ink.7" style={{ letterSpacing: '0.05em' }}>
                    Needs your decision
                  </Text>
                  <Text size="xs" c="dimmed">
                    {pendingInterests.length} pending
                  </Text>
                </Group>
                {!isPublished && pendingInterests.length === 0 ? (
                  <Card withBorder padding="md" radius="md" bg="ink.0">
                    <Text size="sm" c="dimmed">
                      {isDraft
                        ? 'Publish the event to start accepting interests.'
                        : `This event is ${event.state}.`}
                    </Text>
                  </Card>
                ) : pendingInterests.length === 0 ? (
                  <Card withBorder padding="md" radius="md" bg="ink.0">
                    <Text size="sm" c="dimmed">
                      No new interests right now. Artists who raise their hand will appear here.
                    </Text>
                  </Card>
                ) : (
                  <Stack gap="sm">
                    {pendingInterests.map((interest) => (
                      <InterestRow
                        key={interest.id}
                        interest={interest}
                        canDecide={isPublished}
                        onAccept={() => {
                          setAcceptingInterest(interest);
                          setAcceptStartDate(startIso);
                          setAcceptEndDate(endIso);
                          openAccept();
                        }}
                        onDecline={() => {
                          setDecliningInterest(interest);
                          setDeclineReason('');
                          openDecline();
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>

              {resolvedInterests.length > 0 ? (
                <Stack gap="sm">
                  <Divider
                    label={
                      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                        Resolved · {resolvedInterests.length}
                      </Text>
                    }
                    labelPosition="left"
                  />
                  <Stack gap="sm">
                    {resolvedInterests.map((interest) => (
                      <InterestRow
                        key={interest.id}
                        interest={interest}
                        canDecide={false}
                        onAccept={() => {}}
                        onDecline={() => {}}
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Box
              style={{
                position: 'sticky',
                top: 'calc(64px + var(--mantine-spacing-md))',
              }}
            >
              <Stack gap="md">
                <Card withBorder padding="lg" radius="md">
                  <Stack gap="sm">
                    <Title order={5}>What's next</Title>
                    <Stack gap="xs">
                      {isDraft ? (
                        <>
                          <Button
                            onClick={() =>
                              eventAction('Publish', `/api/events/${encodeURIComponent(id)}/publish`)
                            }
                            loading={acting === 'Publish'}
                            variant="gradient"
                            gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                            fullWidth
                          >
                            Publish event
                          </Button>
                          <Button onClick={openEdit} variant="default" fullWidth>
                            Edit draft
                          </Button>
                          <Button
                            onClick={() =>
                              eventAction('Cancel', `/api/events/${encodeURIComponent(id)}/cancel`, {})
                            }
                            loading={acting === 'Cancel'}
                            color="red"
                            variant="subtle"
                            fullWidth
                          >
                            Cancel draft
                          </Button>
                        </>
                      ) : isPublished ? (
                        <>
                          <Button
                            onClick={() =>
                              eventAction('Close', `/api/events/${encodeURIComponent(id)}/close`)
                            }
                            loading={acting === 'Close'}
                            variant="default"
                            fullWidth
                          >
                            Close event
                          </Button>
                          <Button
                            onClick={() =>
                              eventAction(
                                'Cancel',
                                `/api/events/${encodeURIComponent(id)}/cancel`,
                                {},
                              )
                            }
                            loading={acting === 'Cancel'}
                            color="red"
                            variant="subtle"
                            fullWidth
                          >
                            Cancel event
                          </Button>
                        </>
                      ) : (
                        <Text c="dimmed" size="sm">
                          This event is {event.state}. No further actions available.
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                </Card>

                <Card withBorder padding="lg" radius="md">
                  <Stack gap="sm">
                    <Title order={5}>Description</Title>
                    <Text style={{ whiteSpace: 'pre-wrap' }} size="sm">
                      {event.description !== null && event.description.length > 0
                        ? event.description
                        : 'No description yet.'}
                    </Text>
                    {event.payCents !== null ||
                    (event.payNotes !== null && event.payNotes.length > 0) ? (
                      <Text size="xs" c="dimmed">
                        Pay: {formatPay(event.payCents, event.payNotes)}
                      </Text>
                    ) : null}
                  </Stack>
                </Card>
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>
      </Stack>

      <Modal opened={editOpen} onClose={closeEdit} title="Edit draft" size="lg">
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            value={editTitle}
            onChange={(e) => setEditTitle(e.currentTarget.value)}
          />
          <Textarea
            label="Description"
            autosize
            minRows={4}
            value={editDescription}
            onChange={(e) => setEditDescription(e.currentTarget.value)}
          />
          {artForms !== null && artForms.length > 0 ? (
            <Select
              label="Art form"
              description="What kind of work fills this slot?"
              data={artForms.map((af) => ({ value: af.id, label: af.name }))}
              value={editArtFormId}
              onChange={(v) => setEditArtFormId(v ?? '')}
              clearable
            />
          ) : null}
          <Group grow>
            <TextInput
              type="datetime-local"
              label="Starts"
              value={editStarts}
              onChange={(e) => setEditStarts(e.currentTarget.value)}
            />
            <TextInput
              type="datetime-local"
              label="Ends"
              value={editEnds}
              onChange={(e) => setEditEnds(e.currentTarget.value)}
            />
          </Group>
          <NumberInput
            label="Slots open"
            min={1}
            max={50}
            value={editSlots}
            onChange={(v) => setEditSlots(typeof v === 'number' ? v : '')}
          />
          <NumberInput
            label="Guaranteed pay (AUD)"
            min={0}
            max={10_000}
            decimalScale={0}
            value={editPayDollars}
            onChange={(v) => setEditPayDollars(typeof v === 'number' ? v : '')}
          />
          <Textarea
            label="Pay notes"
            autosize
            minRows={2}
            maxLength={500}
            value={editPayNotes}
            onChange={(e) => setEditPayNotes(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeEdit}>
              Cancel
            </Button>
            <Button
              loading={acting === 'Save'}
              onClick={() => {
                void saveEdit();
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={acceptOpen}
        onClose={closeAccept}
        title={acceptingInterest !== null ? `Accept ${acceptingInterest.artist.displayName}` : 'Accept'}
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Accepting opens an exchange so you and the artist can plan the visit together. Confirm
            the dates you have in mind. They default to the event window.
          </Text>
          <Group grow>
            <TextInput
              type="date"
              label="Start"
              value={acceptStartDate}
              onChange={(e) => setAcceptStartDate(e.currentTarget.value)}
            />
            <TextInput
              type="date"
              label="End"
              value={acceptEndDate}
              onChange={(e) => setAcceptEndDate(e.currentTarget.value)}
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeAccept}>
              Cancel
            </Button>
            <Button
              loading={acting === 'Accept'}
              onClick={() => {
                void confirmAccept();
              }}
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Accept and open exchange
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={declineOpen}
        onClose={closeDecline}
        title={
          decliningInterest !== null
            ? `Decline ${decliningInterest.artist.displayName}`
            : 'Decline'
        }
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Optional: a short note we'll pass on. Hosts who write something here tend to keep
            artists coming back.
          </Text>
          <Textarea
            autosize
            minRows={3}
            maxRows={8}
            placeholder="Not the right fit for this date, but I'd love to talk about something later in the year."
            value={declineReason}
            onChange={(e) => setDeclineReason(e.currentTarget.value)}
            maxLength={2000}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDecline}>
              Back
            </Button>
            <Button
              color="red"
              loading={acting === 'Decline'}
              onClick={() => {
                void confirmDecline();
              }}
            >
              Decline
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell>
  );
}

function InterestRow({
  interest,
  canDecide,
  onAccept,
  onDecline,
}: {
  interest: InterestItem;
  canDecide: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group wrap="nowrap" align="flex-start">
            <Avatar
              {...(interest.artist.profilePhotoUrl !== null
                ? { src: interest.artist.profilePhotoUrl }
                : {})}
              radius="xl"
              size="md"
              alt={interest.artist.displayName}
            >
              {initials(interest.artist.displayName)}
            </Avatar>
            <Stack gap={2}>
              {interest.artist.slug !== null ? (
                <Link
                  to="/artists/$slug"
                  params={{ slug: interest.artist.slug }}
                  style={{ fontWeight: 600, textDecoration: 'none', color: 'inherit' }}
                >
                  {interest.artist.displayName}
                </Link>
              ) : (
                <Text fw={600}>{interest.artist.displayName}</Text>
              )}
              {interest.artist.bioExcerpt !== null ? (
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {interest.artist.bioExcerpt}
                </Text>
              ) : null}
            </Stack>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <StateBadge entity="event_interest" state={interest.state} />
          </Group>
        </Group>
        {interest.message !== null && interest.message.length > 0 ? (
          <Paper bg="gray.0" p="sm" radius="sm">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {interest.message}
            </Text>
          </Paper>
        ) : null}
        <Group justify="space-between" wrap="wrap">
          <Text size="xs" c="dimmed">
            Sent {new Date(interest.createdAt).toLocaleDateString()}
          </Text>
          <Group gap="xs">
            {interest.state === 'accepted' && interest.exchangeId !== null ? (
              <Link
                to="/exchanges/$id"
                params={{ id: interest.exchangeId }}
                style={{ textDecoration: 'underline', fontSize: 14 }}
              >
                Open exchange →
              </Link>
            ) : null}
            {canDecide ? (
              <>
                <Button size="xs" variant="default" onClick={onDecline}>
                  Decline
                </Button>
                <Button size="xs" onClick={onAccept}>
                  Accept
                </Button>
              </>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}

function formatRange(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
  }
  return `${dateFmt.format(start)} → ${dateFmt.format(end)}`;
}

function formatPay(cents: number | null, notes: string | null): string {
  if (cents !== null && cents > 0) {
    const dollars = (cents / 100).toFixed(0);
    return notes !== null && notes.length > 0
      ? `$${dollars} AUD · ${notes}`
      : `$${dollars} AUD`;
  }
  return notes ?? 'As discussed.';
}

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  const head = parts[0]?.charAt(0) ?? '';
  const tail = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
  return (head + tail).toUpperCase() || '?';
}

function localFromMs(ms: number): string {
  // datetime-local format: YYYY-MM-DDTHH:mm — uses local time zone.
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isoDateOnly(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
